import { Col, DatePicker, Layout, Row, Select, Table } from "antd";

import { Helmet } from "react-helmet-async";
// import { getProfitAndLossReport } from "../../Utils/ApiCalls";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumbers,
  GetAllAccomodations,
  GetAllShips,
  getDateRange,
  handleChangePage,
  handleExportToExcel,
  handleExportToPDF,
  handlePageSizeChange, 
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { useEffect, useState } from "react";
import { getRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";

const ProfitAndLossReport = () => {
  const [loading, setLoading] = useState(false);
  const dateRange = getDateRange("month");
  const [filterConfig, setFilterConfig]: any = useState({
    page: 1,
    year: dayjs().year(), // Set current year as default
  });

  const [dataSource, setDataSource]: any = useState([]);
  const [branches, setBranches]: any = useState([]);
  const [expenseTypes, setExpenseTypes]: any = useState([]);

  // Define columns configuration with explicit widths to avoid layout stretching
  const columnsConfig = [
    { label: "Account", key: "name", width: 260, fixed: "left" as const, render: (text: any, record: any) => text },
    { label: "Jan", key: "jan", align: "end", width: 90, ellipsis: true },
    { label: "Feb", key: "feb", align: "end", width: 90, ellipsis: true },
    { label: "Mar", key: "mar", align: "end", width: 90, ellipsis: true },
    { label: "Apr", key: "apr", align: "end", width: 90, ellipsis: true },
    { label: "May", key: "may", align: "end", width: 90, ellipsis: true },
    { label: "Jun", key: "jun", align: "end", width: 90, ellipsis: true },
    { label: "Jul", key: "jul", align: "end", width: 90, ellipsis: true },
    { label: "Aug", key: "aug", align: "end", width: 90, ellipsis: true },
    { label: "Sep", key: "sep", align: "end", width: 90, ellipsis: true },
    { label: "Oct", key: "oct", align: "end", width: 90, ellipsis: true },
    { label: "Nov", key: "nov", align: "end", width: 90, ellipsis: true },
    { label: "Dec", key: "dec", align: "end", width: 90, ellipsis: true },
    { label: "Total", key: "total", align: "end", width: 110, ellipsis: true },
  ];

  const totalTableWidth = columnsConfig.reduce((acc: number, col: any) => acc + (col.width || 100), 0);
  const handleClick = (record: any, header: any) => {
    const monthIndex = columnsConfig.findIndex((h) => h.label === header.label);

    if (monthIndex > 0) {
      const year = filterConfig.year;
      const month = monthIndex;
      const startDate = dayjs(`${year}-${String(month).padStart(2, "0")}-01`)
        .startOf("month")
        .format("YYYY-MM-DD");
      const endDate = dayjs(`${year}-${String(month).padStart(2, "0")}-01`)
        .endOf("month")
        .format("YYYY-MM-DD");
      const formattedType = record.parent.toLowerCase().replace(/\s+/g, "_");

      const modalPayload = {
        expense_type_id: record.expense_type_id,
        start_date: startDate,
        end_date: endDate,
        type: formattedType,
        account_type_name: record.name,
      };

      // setModalData([modalPayload]);
      // setShowModal(true);
    }
  };

  // Generate table columns dynamically
  function getTableColumns() {
    return columnsConfig.map((column: any) => ({
      title: column.label,
      dataIndex: column.key,
      key: column.key,
      align: column.align,
      width: column.width,
      fixed: column.fixed,
      render: (text: any, record: any) => {
        // For Type column, just return the text
        if (column.key === "name") {
          return (
            <div
              style={{
                maxWidth: `${column.width || 260}px`,
                fontWeight: record.isParent ? "bold" : "normal",
                whiteSpace: "normal",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              {text}
            </div>
          );
        }

        // For numeric columns
        const isIncomeLoss =
          record.name === "NET PROFIT/LOSS" && column.key !== "name";
        const isNegative = parseFloat(text) < 0;
        const color = isIncomeLoss ? (isNegative ? "red" : "green") : "inherit";
        const isNumber = !isNaN(text) && isFinite(text);  
        const formattedText = isNumber ? formatNumbers(text) : text;

        const isClickable =
          (record.parent === "INCOME" || record.parent === "EXPENSES") &&
          text !== "0.00";

        return (
          <div
            style={{
              color,
              cursor: isClickable ? "pointer" : "inherit",
              fontWeight: record.isParent ? "bold" : "normal",
            }}
            onClick={
              isClickable ? () => handleClick(record, column) : undefined
            }
          >
            {formattedText}
          </div>
        );
      },
    }));
  }

  function normalizePL(raw: any) {
    const income: any = raw.monthly_sales || {};
    const expense: any = raw.monthly_expense || {};
    const profit: any = raw.profit || {};

    // --- Fix INCOME children (must be array)
    if (!Array.isArray(income.children)) {
      income.children = Array.isArray(income.children)
        ? income.children
        : Object.values(income.children || {});
    }

    // --- Fix EXPENSE children (must be array)
    if (!Array.isArray(expense.children)) {
      expense.children = Object.values(expense.children || {});
    }

    // Add parent property
    income.children = income.children.map((c: any) => ({
      ...c,
      parent: "INCOME",
    }));

    expense.children = expense.children.map((c: any) => ({
      ...c,
      parent: "EXPENSES",
    }));

    return [income, expense, profit];
  }

  async function fetchBranches() {
    setBranches([]);
    const response = await getRequest(`branches/get`, true);
    if (response.success) {
      var data = response.data.data.map((info: any) => ({
        ...info,
        branch_id: info.id,
        branch_name: info.name,
      }));
      setBranches(data);
    }
  }

  async function fetchExpenseTypes() {
    setExpenseTypes([]);
    const response = await getRequest(`expense-types/get`, true);
    if (response.success) {
      var data = response.data.data.map((info: any) => ({
        ...info,
        expense_type_id: info.id,
        expense_type_name: info.name,
      }));
      setExpenseTypes(data);
    }
  }

  async function fetchData() {
    setLoading(true);
    try {
      // var response = await getRequest(`reports/get_upgrade`, filterConfig, true);

      // if (response?.success) {
      //   const raw = response.data?.data || {};
      //   const finalData = normalizePL(raw);

      //   setDataSource(finalData);
      // } else {
      //   setDataSource([]);
      // }

      const sampleData = {
        "data": {
            "monthly_sales": {
                "name": "INCOME",
                "children": [
                    {
                        "id": "Cash",
                        "name": "Cash",
                        "expense_type_id": "Cash",
                        "parent": "INCOME",
                        "total_amount": 450,
                        "jan": "450.00",
                        "feb": "0.00",
                        "mar": "0.00",
                        "apr": "0.00",
                        "may": "0.00",
                        "jun": "0.00",
                        "jul": "0.00",
                        "aug": "0.00",
                        "sep": "0.00",
                        "oct": "0.00",
                        "nov": "0.00",
                        "dec": "0.00",
                        "total": "450.00"
                    }
                ],
                "total": "450.00",
                "jan": "450.00",
                "feb": "0.00",
                "mar": "0.00",
                "apr": "0.00",
                "may": "0.00",
                "jun": "0.00",
                "jul": "0.00",
                "aug": "0.00",
                "sep": "0.00",
                "oct": "0.00",
                "nov": "0.00",
                "dec": "0.00"
            },
            "monthly_expense": {
                "name": "EXPENSES",
                "key": "EXPENSES",
                "children": [
                    {
                        "name": "Emborg Whole Milk",
                        "total_amount": 33190,
                        "jan": 33190,
                        "feb": 0,
                        "mar": 0,
                        "apr": 0,
                        "may": 0,
                        "jun": 0,
                        "jul": 0,
                        "aug": 0,
                        "sep": 0,
                        "oct": 0,
                        "nov": 0,
                        "dec": 0
                    }
                ],
                "total": "33,190.00",
                "jan": "33,190.00",
                "feb": "0.00",
                "mar": "0.00",
                "apr": "0.00",
                "may": "0.00",
                "jun": "0.00",
                "jul": "0.00",
                "aug": "0.00",
                "sep": "0.00",
                "oct": "0.00",
                "nov": "0.00",
                "dec": "0.00"
            },
            "profit": {
                "name": "NET PROFIT/LOSS",
                "key": "NET PROFIT/LOSS",
                "total": "-32,740.00",
                "jan": "-32,740.00",
                "feb": "0.00",
                "mar": "0.00",
                "apr": "0.00",
                "may": "0.00",
                "jun": "0.00",
                "jul": "0.00",
                "aug": "0.00",
                "sep": "0.00",
                "oct": "0.00",
                "nov": "0.00",
                "dec": "0.00"
            }
        }
      };

      const raw = sampleData;
      const finalData = normalizePL(raw);

      setDataSource([finalData]);

    } catch (error) {
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  }

  const handleYearChange = (date: any, dateString: string | string[]) => {
    const selectedYear = date ? date.year() : null;
    setFilterConfig((prev: any) => ({
      ...prev,
      year: selectedYear,
    }));
  }

  useEffect(() => {
    fetchBranches();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  return (
    <Layout className="page">
      <Helmet>
        <title>VAR CTPL Insurance System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={23} className="px-5">
            <PageHeader
              title="Profit and Loss Report"
              withDateRangeFilter={false}
              withAddButton={false}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              withSearchBtn={false}
            />
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-1 px-5">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col span={24} className="mb-3 px-5">
            <Row>
              <Col xs={24} md={12} lg={4} className="mb-2 me-2 mb-lg-0">
                <DatePicker
                  picker="year"
                  onChange={handleYearChange}
                  value={
                    filterConfig.year ? dayjs().year(filterConfig.year) : null
                  }
                  format="YYYY"
                  allowClear
                  className="w-full"
                  placeholder="Select Year"
                />
              </Col>
              <Col xs={24} md={12} lg={4} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  showSearch
                  placeholder="Select branch"
                  value={filterConfig.branch_id || ""}
                  onChange={(value) => setFilterConfig((prev: any) => ({ ...prev, branch_id: value }))}
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Select.Option value="">All Branches</Select.Option>
                  {branches.map((branch: any) => (
                    <Select.Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>
          <Col xs={24} sm={24} md={22} lg={22} xl={22}>
            <div className="table-container border rounded-lg">
              <div className="custom-table table-wrapper">
                <Table
                  pagination={false}
                  loading={loading}
                  columns={getTableColumns()}
                  dataSource={dataSource}
                  scroll={{ x: totalTableWidth }}
                  style={{ minWidth: 500}}
                  tableLayout="fixed"
                  size="middle"
                  rowKey={(record, index) => record.name || index}
                  bordered
                  sticky
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default ProfitAndLossReport;
